import React from "react";
import { createRoot } from "react-dom/client";
import "sweetalert2/dist/sweetalert2.min.css";
import "./index.scss";
import { Amplify } from "aws-amplify";
import config from "./config";
import App from "./App";
import { BrowserRouter as Router } from "react-router-dom";
import { Provider as ReduxProvider } from "react-redux";
import { store } from "./store/store";
import { AuthProvider } from "./authentication/authHelpers";
import { LicenseManager } from "ag-grid-enterprise";
import { StyleSheetManager } from "styled-components";
import isPropValid from "@emotion/is-prop-valid";
import { initPostHog } from "./analytics/posthogClient";
import {
  bindNotificationClickNavigation,
  registerNotificationServiceWorker,
} from "./utils/notificationServiceWorker";

initPostHog();

// Force HTTPS for production hosts — HTTP breaks Cognito-signed APIs (403),
// service workers, and browser/OS notifications.
if (typeof window !== "undefined") {
  const host = window.location.hostname || "";
  const isProdHost =
    host === "portal1.flex.al" ||
    host === "flex1.flex.al" ||
    host === "test.flex.al" ||
    host.endsWith(".flex.al");
  if (isProdHost && window.location.protocol === "http:") {
    const httpsUrl =
      "https://" +
      window.location.host +
      window.location.pathname +
      window.location.search +
      window.location.hash;
    window.location.replace(httpsUrl);
  }
}

// Tray / OS notifications (Gmail-style) need a service worker registration.
if (typeof window !== "undefined" && window.isSecureContext) {
  bindNotificationClickNavigation();
  if (document.readyState === "complete") {
    void registerNotificationServiceWorker();
  } else {
    window.addEventListener("load", () => {
      void registerNotificationServiceWorker();
    });
  }
}

// styled-components v6 stopped auto-filtering unknown props on plain DOM
// elements. Local spinner shim + any leftover styled deps rely on filtering.
const shouldForwardProp = (propName, target) =>
  typeof target === "string" ? isPropValid(propName) : true;

if (process.env.NODE_ENV === "production") {
  console.log = () => {};
  console.debug = () => {};
}

// AG Grid Enterprise license — renewed key (AG-060860), valid for
// versions released before 27 July 2025.
LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-060860}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Core_Scaffold_Systems_Inc}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Lead_Manager}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Lead_Manager}_need_to_be_licensed___{Lead_Manager}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{27_July_2025}____[v3]_[01]_MTc1MzU3MDgwMDAwMA==83ebbaa26e1c77e90d30bdaf2233f77f"
);

const endpointNames = [
  "employees",
  "departments",
  "clocking",
  "employeeRequests",
  "programFields",
  "documentation",
  "hotCredentials",
  "filterTables",
  "docConfiguration",
  "shifts",
  "earnings",
  "deductions",
  "clocking",
  "notifications",
  "userGroups",
  "users",
  "filterTables",
  "clockingLogs",
  "leave",
  "getIp",
  "wages",
  "prepayments",
  "reportsLogs",
  "lazyFetching",
  "employeeOvertime",
  "announcements",
  "interviews",
  "tickets",
  "officeDuties",
  "quoteOfTheDay",
  "shiftRotationPolicies",
  "shiftSchedules",
  "gmailInbox",
  "security",
];

const oauthDomain =
  process.env.REACT_APP_COGNITO_DOMAIN ||
  "flexhr-prod-eu.auth.eu-central-1.amazoncognito.com";
const oauthRedirectSignIn =
  process.env.REACT_APP_OAUTH_REDIRECT_SIGN_IN ||
  (typeof window !== "undefined"
    ? `${window.location.origin}/`
    : "http://localhost:3000/");
const oauthRedirectSignOut =
  process.env.REACT_APP_OAUTH_REDIRECT_SIGN_OUT ||
  (typeof window !== "undefined"
    ? `${window.location.origin}/`
    : "http://localhost:3000/");

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.cognito.USER_POOL_ID,
      userPoolClientId: config.cognito.APP_CLIENT_ID,
      identityPoolId: config.cognito.IDENTITY_POOL_ID,
      loginWith: {
        oauth: {
          domain: oauthDomain,
          scopes: [
            "phone",
            "email",
            "profile",
            "openid",
            "aws.cognito.signin.user.admin",
          ],
          redirectSignIn: [oauthRedirectSignIn],
          redirectSignOut: [oauthRedirectSignOut],
          responseType: "token",
        },
      },
    },
  },
  API: {
    REST: Object.fromEntries(
      endpointNames.map((name) => [
        name,
        {
          endpoint: config.apiGateway.URL,
          region: config.apiGateway.REGION,
        },
      ])
    ),
  },
});

const root = createRoot(document.getElementById("root"));

root.render(
  <StyleSheetManager shouldForwardProp={shouldForwardProp}>
    <ReduxProvider store={store}>
      <Router>
        <AuthProvider>
          <App />
        </AuthProvider>
      </Router>
    </ReduxProvider>
  </StyleSheetManager>
);
